// DEPLOY: /var/www/woody.wtf/frontend/src/components/SignalOutcomes.js
// Signal Chart tab: what price did after each signal fired. Data from
// GET /api/strategy/strategies/<id>/signal-outcomes (reads the SAVED strategy).
// Entry = next traded 1m open after the firing (same fill rule as the backtest).
import React, { useState } from 'react';
import api from '../api';

// New York calendar/time helpers (signals and sessions are NY-anchored); shared with SignalChart.
export const nyToday = () => new Date().toLocaleDateString('en-CA',{timeZone:'America/New_York'});
export const nyDate  = iso => new Date(iso).toLocaleDateString('en-CA',{timeZone:'America/New_York'});
export const addDays = (iso,n) => { const d=new Date(iso+'T12:00:00Z'); d.setUTCDate(d.getUTCDate()+n); return d.toISOString().slice(0,10); };
export const fmtNY   = ms => new Date(ms).toLocaleString('en-AU',{timeZone:'America/New_York',weekday:'short',day:'numeric',month:'short',hour:'2-digit',minute:'2-digit',hour12:false});

const MAX_DAYS = 366;    // mirrors SIGNAL_OUTCOMES_MAX_DAYS in strategy.py
const MAX_ROWS = 2000;   // rendered rows; sort/filter to see the rest
const COLS = [['m5','+5m'],['m15','+15m'],['m30','+30m'],['m60','+60m'],['close','Close'],['up','Max up 1h'],['down','Max down 1h'],['hold','Hold']];
const SUMMARY = ['m5','m15','m30','m60','close','hold'];

const pctClr = v => v==null?'var(--text-muted)':v>0?'var(--up)':v<0?'var(--down)':'var(--text)';
const fmtP   = v => v==null?'-':(v>0?'+':'')+v.toFixed(2)+'%';
const median = a => { const s=[...a].sort((x,y)=>x-y), m=s.length>>1; return s.length%2?s[m]:(s[m-1]+s[m])/2; };

export default function SignalOutcomes({activeId, saveStrategy, ext, onPick, styles}) {
  const {sec, sBody, lbl, inp, sel, btn} = styles;
  const [from,    setFrom]    = useState(()=>addDays(nyToday(),-91));
  const [to,      setTo]      = useState(nyToday);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [tkFilt,  setTkFilt]  = useState('');
  const [sort,    setSort]    = useState({key:'fired_at',dir:-1});

  const run = async()=>{
    if(!activeId){setError('Save the strategy first.');return;}
    if(!await saveStrategy())return;   // the engine reads the saved signals
    setLoading(true);setError('');
    try{ setData((await api.get(`/strategy/strategies/${activeId}/signal-outcomes`,{params:{from,to}})).data); }
    catch(e){ setData(null);setError(e.response?.data?.error||'Failed.'); }
    finally{ setLoading(false); }
  };

  const rows=(data?.rows||[]).filter(r=>(ext||!r.extended)&&(!tkFilt||r.ticker===tkFilt));
  const tickers=[...new Set((data?.rows||[]).map(r=>r.ticker))];
  const sorted=[...rows].sort((a,b)=>{
    const x=a[sort.key], y=b[sort.key];
    if(x==null)return 1; if(y==null)return -1;   // blanks last either way
    return (x<y?-1:x>y?1:0)*sort.dir;
  });
  const sortBy=key=>setSort(s=>({key,dir:s.key===key?-s.dir:-1}));

  // Summary per signal x ticker: n, avg / median, win % (moves > 0) for each horizon.
  const groups={};
  rows.forEach(r=>{(groups[r.signal_id+'|'+r.ticker]=groups[r.signal_id+'|'+r.ticker]||{r,list:[]}).list.push(r);});
  const stat=(list,k)=>{const v=list.map(r=>r[k]).filter(x=>x!=null);return v.length?{avg:v.reduce((a,b)=>a+b,0)/v.length,med:median(v),win:v.filter(x=>x>0).length/v.length*100,n:v.length}:null;};

  const th={padding:'5px 8px',fontSize:'10px',fontWeight:700,color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace",textAlign:'right',whiteSpace:'nowrap',borderBottom:'1px solid var(--border)',position:'sticky',top:0,background:'var(--surface)',cursor:'pointer',userSelect:'none'};
  const td={padding:'4px 8px',fontSize:'11px',fontFamily:"'JetBrains Mono',monospace",textAlign:'right',whiteSpace:'nowrap',borderBottom:'1px solid var(--border)'};
  const thL={...th,textAlign:'left'}, tdL={...td,textAlign:'left'};
  const arrow=k=>sort.key===k?(sort.dir>0?' ▲':' ▼'):'';
  const dot=c=><span style={{display:'inline-block',width:'8px',height:'8px',borderRadius:'50%',background:c||'var(--success)',marginRight:'5px'}}/>;

  return (
    <div style={{...sec,marginTop:'10px'}}>
      <div style={sBody}>
        <div style={{display:'flex',gap:'10px',alignItems:'flex-end',flexWrap:'wrap'}}>
          <div style={{fontSize:'11px',fontWeight:700,fontFamily:"'JetBrains Mono',monospace",textTransform:'uppercase',marginBottom:'6px'}}>Signal outcomes</div>
          <div><label style={lbl}>From (NY)</label><input type="date" style={inp} value={from} onChange={e=>setFrom(e.target.value)}/></div>
          <div><label style={lbl}>To (max {MAX_DAYS} days)</label><input type="date" style={inp} value={to} onChange={e=>setTo(e.target.value)}/></div>
          {tickers.length>1&&<div><label style={lbl}>Ticker</label>
            <select style={sel} value={tkFilt} onChange={e=>setTkFilt(e.target.value)}>
              <option value="">All</option>{tickers.map(t=><option key={t} value={t}>{t}</option>)}
            </select></div>}
          <button style={btn} onClick={run} disabled={loading||!activeId} title="Saves the strategy first; outcomes use the saved signals">{loading?'Running…':'Run outcomes'}</button>
        </div>
        <div style={{fontSize:'10px',color:'var(--text-muted)',marginTop:'6px'}}>
          Moves are from the next traded 1-minute open after the signal fires. Close = that day's 4pm close (next session's close for post-market firings). Max up/down = furthest the high/low went within the first hour. Hold = until the next signal into a different regime (blank if none within 31 days). Measured on the signal's own tickers and the target regime's holdings. Follows the pre/post toggle above. Click a row to chart that day.
          {data?.skipped_signals?.length>0&&` Not measured (SMC): ${data.skipped_signals.join(', ')}.`}
        </div>
        {error&&<div style={{fontSize:'11px',color:'var(--error)',marginTop:'6px'}}>{error}</div>}

        {data&&Object.keys(groups).length>0&&(
          <div style={{overflowX:'auto',marginTop:'10px'}}>
            <table style={{borderCollapse:'collapse',width:'100%'}}>
              <thead><tr>
                <th style={{...thL,cursor:'default'}}>Signal</th><th style={{...thL,cursor:'default'}}>Ticker</th><th style={{...th,cursor:'default'}}>N</th>
                {SUMMARY.map(k=><th key={k} style={{...th,cursor:'default'}}>{COLS.find(c=>c[0]===k)[1]}<div style={{fontWeight:400}}>avg / med · win</div></th>)}
              </tr></thead>
              <tbody>{Object.values(groups).map(({r,list})=>(
                <tr key={r.signal_id+r.ticker}>
                  <td style={tdL}>{dot(r.color)}{r.label||'Signal'}</td><td style={tdL}>{r.ticker}</td><td style={td}>{list.length}</td>
                  {SUMMARY.map(k=>{const s=stat(list,k);return <td key={k} style={td}>{s?<>
                    <span style={{color:pctClr(s.avg)}}>{fmtP(s.avg)}</span> / <span style={{color:pctClr(s.med)}}>{fmtP(s.med)}</span>
                    <div style={{fontSize:'10px',color:'var(--text-muted)'}}>{s.win.toFixed(0)}%{s.n<list.length?` (n=${s.n})`:''}</div></>:'-'}</td>;})}
                </tr>))}
              </tbody>
            </table>
          </div>
        )}

        {data&&(
          <div style={{overflow:'auto',maxHeight:'60vh',marginTop:'10px',border:'1px solid var(--border)',borderRadius:'var(--radius)'}}>
            {!rows.length&&<div style={{padding:'10px',fontSize:'12px',color:'var(--text-muted)'}}>No firings in this range.</div>}
            {rows.length>0&&<table style={{borderCollapse:'collapse',width:'100%'}}>
              <thead><tr>
                <th style={thL} onClick={()=>sortBy('fired_at')}>Fired (NY){arrow('fired_at')}</th>
                <th style={thL} onClick={()=>sortBy('label')}>Signal{arrow('label')}</th>
                <th style={thL} onClick={()=>sortBy('target_regime')}>→ Regime{arrow('target_regime')}</th>
                <th style={thL} onClick={()=>sortBy('ticker')}>Ticker{arrow('ticker')}</th>
                <th style={th} onClick={()=>sortBy('entry')}>Entry{arrow('entry')}</th>
                {COLS.map(([k,l])=><th key={k} style={th} onClick={()=>sortBy(k)}>{l}{arrow(k)}</th>)}
              </tr></thead>
              <tbody>{sorted.slice(0,MAX_ROWS).map((r,i)=>(
                <tr key={i} onClick={()=>onPick(nyDate(r.fired_at))} style={{cursor:'pointer'}} title="Chart this day">
                  <td style={tdL}>{fmtNY(Date.parse(r.fired_at))}{r.extended&&<span style={{color:'var(--text-muted)'}}> ext</span>}</td>
                  <td style={tdL}>{dot(r.color)}{r.label||'Signal'}{r.conflict&&<span style={{color:'var(--warning)'}}> conflict</span>}</td>
                  <td style={tdL}>{r.target_regime}</td><td style={tdL}>{r.ticker}</td>
                  <td style={td}>{r.entry.toFixed(2)}</td>
                  {COLS.map(([k])=><td key={k} style={{...td,color:pctClr(r[k])}}>{fmtP(r[k])}</td>)}
                </tr>))}
              </tbody>
            </table>}
            {sorted.length>MAX_ROWS&&<div style={{padding:'6px 10px',fontSize:'11px',color:'var(--text-muted)'}}>Showing {MAX_ROWS} of {sorted.length} rows — sort or filter to see others.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
